import { ReactNode, useEffect } from "react";
import styled from "styled-components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Button,
  IconButton,
  Input,
  Modal,
  Stack,
  Text,
} from "@fidelix/fx-miranda";
import { Controller, useForm } from "react-hook-form";
import { Heading } from "react-aria-components";

export type EditFieldDetail = {
  name: string;
  label: ReactNode;
  defaultValue: string;
  required?: boolean;
};

type EditFormModalProps = {
  onClose: () => void;
  title: ReactNode;
  fields: EditFieldDetail[];
  onSubmit: (values: Record<string, string>) => void | Promise<void>;
  errors?: Record<string, string>;
  isPending?: boolean;
};

export function EditFormModal({
  onClose,
  title,
  fields,
  onSubmit,
  errors,
  isPending = false,
}: EditFormModalProps) {
  const defaultValues = fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.name] = field.defaultValue;
    return acc;
  }, {});

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isValid },
  } = useForm<Record<string, string>>({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    fields.forEach((field) => {
      const message = errors?.[field.name];
      if (message) {
        setError(field.name, { type: "server", message });
      } else {
        clearErrors(field.name);
      }
    });
  }, [clearErrors, errors, fields, setError]);

  return (
    <Modal onClose={onClose} placement="middle">
      <Title slot="title">
        <Text variant="title2Bold">{title}</Text>
        <IconButton
          icon="close"
          size="small"
          variant="transparent"
          aria-label={t`Close`}
          onPress={onClose}
          isDisabled={isPending}
        />
      </Title>
      <Body>
        {fields.map((field) => (
          <Controller
            key={field.name}
            control={control}
            name={field.name}
            rules={
              field.required !== false
                ? { required: t`Field is required` }
                : undefined
            }
            render={({ field: formField, fieldState }) => (
              <FormRow>
                <Text variant="bodyBold">{field.label}</Text>
                <FormInput
                  value={formField.value}
                  onChange={formField.onChange}
                  errorMessage={fieldState.error?.message}
                />
              </FormRow>
            )}
          />
        ))}
      </Body>
      <EditFormModalFooter>
        <Stack axis="x" spacing="medium" justify="end">
          <Button variant="text" onPress={onClose} isDisabled={isPending}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            onPress={() => handleSubmit(onSubmit)()}
            isDisabled={!isValid || isPending}
            isLoading={isPending}
            iconPlacement="left"
          >
            <Trans>Save</Trans>
          </Button>
        </Stack>
      </EditFormModalFooter>
    </Modal>
  );
}

const Title = styled(Heading)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  margin: 30px;
`;

const Body = styled(Modal.Body)`
  display: grid;
  grid-template-columns: 1fr;
  margin: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  grid-column-gap: ${(p) => p.theme.space("large")};
  align-items: center;
`;

const FormInput = styled(Input)`
  & > div {
    width: 500px;
  }
`;

const EditFormModalFooter = styled(Modal.Footer)`
  display: flex;
  justify-content: flex-end;
`;
